import { useCallback, useRef, useState } from 'react';
import { createAsyncActionLock } from '@shared/common/asyncActionLock';

type AsyncAction<TArgs extends unknown[], TResult> = (...args: TArgs) => TResult | Promise<TResult>;

/**
 * Prevents a UI action from starting again while its previous invocation is pending.
 * The ref is the actual lock; state is only used to reflect the disabled state in the UI.
 */
export default function useAsyncActionLock<TArgs extends unknown[], TResult>(action: AsyncAction<TArgs, TResult>) {
    const actionRef = useRef(action);
    const lockRef = useRef(createAsyncActionLock());
    const [isInFlight, setIsInFlight] = useState(false);

    actionRef.current = action;

    const run = useCallback(async (...args: TArgs): Promise<TResult | undefined> => {
        if (lockRef.current.isInFlight) {
            return undefined;
        }

        setIsInFlight(true);
        try {
            return await lockRef.current.run(() => actionRef.current(...args));
        } finally {
            setIsInFlight(false);
        }
    }, []);

    return { run, isInFlight };
}
